<script>
  import { navigate } from '../../lib/stores/router.svelte.js';
  import {
    authState,
    signOutUser,
    isUserAnonymous,
    initiateDiscordOAuth,
  } from '../../lib/stores/auth.svelte.js';
  import { getBinderColor } from '../../lib/logic/utils.js';
  import { selfWantListHandle } from '../../lib/logic/wantlist-logic.js';
  import {
    normalizeToShortUrl,
    pinBinder,
    unpinBinder,
    claimBinder,
    unclaimBinder,
  } from '../../lib/services/recent-binders.js';
  import { MAX_MY_BINDERS } from '../../lib/logic/config.js';
  import { sectiondrag } from '../../lib/actions/sectiondrag.js';
  import { cancelTrade } from '../../lib/services/trade-service.js';
  import { tradeState } from '../../lib/stores/trade.svelte.js';
  import { sidebarState, loadSidebarData, isPinned } from '../../lib/stores/sidebar.svelte.js';
  import ContextMenu from '../shared/ContextMenu.svelte';

  let { isOpen = $bindable(false) } = $props();

  // Trade context menu state
  let tradeMenuOpen = $state(false);
  let tradeMenuAnchor = $state(null);
  let tradeMenuTrade = $state(null);

  function showTradeMenu(trade, e) {
    e.preventDefault();
    e.stopPropagation();
    tradeMenuTrade = trade;
    tradeMenuAnchor = e.currentTarget;
    tradeMenuOpen = true;
  }

  async function handleCancelTrade() {
    if (!tradeMenuTrade?.tradeId) return;
    try {
      await cancelTrade(tradeMenuTrade.tradeId);
      await loadSidebarData();
      // Notify other components (e.g., HomePage) that binder list changed
      window.dispatchEvent(new CustomEvent('recentBindersUpdated'));
    } catch (err) {
      console.error('[Sidebar] Failed to cancel trade:', err);
    }
  }

  const tradeMenuItems = $derived([
    {
      label: 'Cancel Trade',
      variant: 'warning',
      onClick: handleCancelTrade,
    },
  ]);

  // Section refs for drag-and-drop
  let myBindersSection = $state(null);
  let recentBindersSection = $state(null);
  let sections = $derived({ my: myBindersSection, recent: recentBindersSection });

  // Convenience aliases from preloaded store
  let recentBinders = $derived(sidebarState.recentBinders);
  let myBinders = $derived(sidebarState.myBinders);
  let openTrades = $derived(sidebarState.openTrades);
  let awaitingTrades = $derived(sidebarState.awaitingTrades);

  // Refresh data when sidebar opens (background refresh, data already preloaded)
  $effect(() => {
    if (isOpen && authState.ready) {
      loadSidebarData();
    }
  });

  async function togglePin(url) {
    const normalized = normalizeToShortUrl(url);
    if (isPinned(url)) {
      unpinBinder(normalized);
      sidebarState.pinnedUrls = sidebarState.pinnedUrls.filter(
        (u) => u !== normalized.toLowerCase()
      );
    } else {
      pinBinder(normalized);
      sidebarState.pinnedUrls = [...sidebarState.pinnedUrls, normalized.toLowerCase()];
    }
  }

  async function handleBinderDrop(binderData, targetSection) {
    if (targetSection === 'my') {
      if (myBinders.length >= MAX_MY_BINDERS) return;
      const success = await claimBinder({
        url: binderData.url,
        name: binderData.name,
        owner: binderData.owner,
      });
      if (success) {
        await loadSidebarData();
        window.dispatchEvent(new CustomEvent('recentBindersUpdated'));
      }
    } else if (targetSection === 'recent') {
      await unclaimBinder(binderData.url);
      await loadSidebarData();
      window.dispatchEvent(new CustomEvent('recentBindersUpdated'));
    }
  }

  // Close on Escape
  function handleKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      isOpen = false;
    }
  }

  function close() {
    isOpen = false;
  }

  function goHome() {
    navigate('home');
    close();
  }

  function goSearch() {
    navigate('search');
    close();
  }

  function goBinders() {
    navigate('binders');
    close();
  }

  function goGroups() {
    navigate('groups');
    close();
  }

  function goSettings() {
    navigate('settings');
    close();
  }

  async function handleSignOut() {
    try {
      await signOutUser();
    } catch (e) {
      console.error('[Sidebar] Sign out failed:', e);
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- Hamburger Toggle -->
<button
  class="sidebar-toggle"
  class:open={isOpen}
  aria-label={isOpen ? 'Close menu' : 'Open menu'}
  aria-expanded={isOpen}
  onclick={() => (isOpen = !isOpen)}
>
  <span class="hamburger-line"></span>
  <span class="hamburger-line"></span>
  <span class="hamburger-line"></span>
</button>

<!-- Backdrop -->
{#if isOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="sidebar-backdrop"
    class:visible={isOpen}
    onclick={close}
    onkeydown={handleKeydown}
  ></div>
{/if}

<!-- Sidebar Panel -->
<nav class="sidebar" class:open={isOpen} aria-label="Main navigation" aria-hidden={!isOpen}>
  <div class="sidebar-header">
    <button class="sidebar-title" onclick={goHome}>
      <img src="/bb_logo.svg" alt="" class="sidebar-logo" />
      Binder Browser
    </button>
    <button class="sidebar-close" aria-label="Close menu" onclick={close}>
      <svg viewBox="0 0 24 24">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  </div>

  <div class="sidebar-content">
    <!-- Account Section -->
    <section class="sidebar-section sidebar-account">
      {#if isUserAnonymous()}
        <div class="sidebar-account-signed-out">
          <button class="sidebar-signin-btn" onclick={() => initiateDiscordOAuth()}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path
                d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
              />
            </svg>
            Sign in with Discord
          </button>
          <p class="sidebar-signin-hint">Sign in to sync your data across devices</p>
        </div>
      {:else if authState.user}
        <div class="sidebar-account-signed-in">
          <div class="sidebar-user-avatar">
            {#if authState.user.photoURL}
              <img src={authState.user.photoURL} alt="" referrerpolicy="no-referrer" />
            {/if}
          </div>
          <div class="sidebar-user-info">
            <div class="sidebar-user-name">{authState.user.displayName || 'User'}</div>
            <div class="sidebar-user-email">{authState.user.email || ''}</div>
          </div>
          <button class="sidebar-signout-btn" onclick={handleSignOut}>Sign out</button>
        </div>
      {/if}
    </section>

    <hr class="sidebar-divider" />

    <!-- Navigation Links -->
    <button class="sidebar-search-link" onclick={goSearch}>
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      Search across binders
    </button>
    <button class="sidebar-search-link" onclick={goBinders}>
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <rect x="2" y="3" width="20" height="14" rx="2"></rect>
        <path d="M8 21h8M12 17v4"></path>
      </svg>
      All Binders
    </button>
    {#if !isUserAnonymous()}
      <button class="sidebar-search-link" onclick={goGroups}>
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
        Groups
      </button>
      <button class="sidebar-search-link" onclick={goSettings}>
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="3"></circle>
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
          ></path>
        </svg>
        Settings
      </button>
    {/if}

    <hr class="sidebar-divider" />

    <!-- Want List Section — the explicit, user-managed Want List (ADR-0009).
         Replaces the retired deck-designated want list. Links to the owner's
         own want page; managed there and via the in-binder star. -->
    {#if !isUserAnonymous()}
      <section class="sidebar-section">
        <h3 class="sidebar-section-title">Want List</h3>
        <ul class="sidebar-list">
          <li>
            <div
              class="sidebar-list-item"
              onclick={() => {
                navigate('wantlist', { handle: selfWantListHandle(authState.discordUsername) });
                close();
              }}
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  navigate('wantlist', { handle: selfWantListHandle(authState.discordUsername) });
                  close();
                }
              }}
              tabindex="0"
              role="button"
            >
              <div class="sidebar-item-icon" style="background: #fbbf24; color: #1a1a1a;">★</div>
              <div class="sidebar-item-content">
                <div class="sidebar-item-name">My want list</div>
              </div>
            </div>
          </li>
        </ul>
      </section>

      <hr class="sidebar-divider" />
    {/if}

    <!-- Trades Section -->
    <section class="sidebar-section">
      <h3 class="sidebar-section-title">Trades</h3>
      {#if awaitingTrades.length > 0}
        <div class="sidebar-subsection">
          <div class="sidebar-subsection-title">Awaiting Exchange</div>
          <ul class="sidebar-list">
            {#each awaitingTrades as trade (trade.tradeId)}
              {@const color = getBinderColor(trade.binderName || 'Trade')}
              <li>
                <div
                  class="sidebar-list-item"
                  class:active-trade={tradeState.currentTradeId === trade.tradeId}
                  onclick={() => {
                    navigate('binder', { tradeId: trade.tradeId, tradeMode: true });
                    close();
                  }}
                  onkeydown={(e) => {
                    if (e.key === 'Enter') {
                      navigate('binder', { tradeId: trade.tradeId, tradeMode: true });
                      close();
                    }
                  }}
                  tabindex="0"
                  role="button"
                >
                  <div class="sidebar-item-icon" style="background: {color}20; color: {color};">
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
                    </svg>
                  </div>
                  <div class="sidebar-item-content">
                    <div class="sidebar-item-name">{trade.binderName || 'Trade'}</div>
                    {#if trade.ownerName}
                      <div class="sidebar-item-meta">{trade.ownerName}</div>
                    {/if}
                  </div>
                  {#if trade.exchangeProgress}
                    <span class="sidebar-exchange-progress"
                      >{trade.exchangeProgress.exchanged}/{trade.exchangeProgress.total}</span
                    >
                  {/if}
                  <button
                    class="sidebar-trade-menu-btn"
                    title="More options"
                    onclick={(e) => showTradeMenu(trade, e)}>&#8942;</button
                  >
                </div>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
      {#if openTrades.length > 0}
        <div class="sidebar-subsection">
          <div class="sidebar-subsection-title">In Progress</div>
          <ul class="sidebar-list">
            {#each openTrades as trade (trade.tradeId)}
              {@const color = getBinderColor(trade.binderName || 'Trade')}
              <li>
                <div
                  class="sidebar-list-item"
                  class:active-trade={tradeState.currentTradeId === trade.tradeId}
                  onclick={() => {
                    navigate('binder', { tradeId: trade.tradeId, tradeMode: true });
                    close();
                  }}
                  onkeydown={(e) => {
                    if (e.key === 'Enter') {
                      navigate('binder', { tradeId: trade.tradeId, tradeMode: true });
                      close();
                    }
                  }}
                  tabindex="0"
                  role="button"
                >
                  <div class="sidebar-item-icon" style="background: {color}20; color: {color};">
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
                    </svg>
                  </div>
                  <div class="sidebar-item-content">
                    <div class="sidebar-item-name">{trade.binderName || 'Trade'}</div>
                    {#if trade.ownerName}
                      <div class="sidebar-item-meta">{trade.ownerName}</div>
                    {/if}
                  </div>
                  <button
                    class="sidebar-trade-menu-btn"
                    title="More options"
                    onclick={(e) => showTradeMenu(trade, e)}>&#8942;</button
                  >
                </div>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
      {#if openTrades.length === 0 && awaitingTrades.length === 0}
        <p class="sidebar-empty">No active trades</p>
      {/if}
    </section>

    <!-- My Binders Section -->
    <section class="sidebar-section">
      <h3 class="sidebar-section-title">
        My Binders <span class="sidebar-slot-count">{myBinders.length}/{MAX_MY_BINDERS}</span>
      </h3>
      <div class="sidebar-drop-zone" bind:this={myBindersSection}>
        {#if myBinders.length > 0}
          <ul class="sidebar-list">
            {#each myBinders as binder (binder.url)}
              {@const color = getBinderColor(binder.name)}
              <li use:sectiondrag={{ onDrop: handleBinderDrop, binderData: binder, sections }}>
                <div
                  class="sidebar-list-item"
                  onclick={() => {
                    navigate('binder', { binderUrl: binder.url, shortPath: binder.url });
                    close();
                  }}
                  onkeydown={(e) => {
                    if (e.key === 'Enter') {
                      navigate('binder', { binderUrl: binder.url, shortPath: binder.url });
                      close();
                    }
                  }}
                  tabindex="0"
                  role="button"
                >
                  <div class="sidebar-item-icon" style="background: {color};">
                    {(binder.name?.charAt(0) || 'B').toUpperCase()}
                  </div>
                  <div class="sidebar-item-content">
                    <div class="sidebar-item-name">{binder.name || 'Unknown'}</div>
                    {#if binder.owner}
                      <div class="sidebar-item-meta">{binder.owner}</div>
                    {/if}
                  </div>
                </div>
              </li>
            {/each}
          </ul>
        {:else}
          <p class="sidebar-empty">Drag a binder here to claim it</p>
        {/if}
      </div>
    </section>

    <hr class="sidebar-divider" />

    <!-- Recent Binders Section -->
    <section class="sidebar-section">
      <h3 class="sidebar-section-title">Recent Binders</h3>
      <div class="sidebar-drop-zone" bind:this={recentBindersSection}>
        {#if recentBinders.length > 0}
          <ul class="sidebar-list">
            {#each recentBinders as binder (binder.url)}
              {@const color = getBinderColor(binder.name)}
              <li use:sectiondrag={{ onDrop: handleBinderDrop, binderData: binder, sections }}>
                <div
                  class="sidebar-list-item"
                  onclick={() => {
                    navigate('binder', { binderUrl: binder.url, shortPath: binder.url });
                    close();
                  }}
                  onkeydown={(e) => {
                    if (e.key === 'Enter') {
                      navigate('binder', { binderUrl: binder.url, shortPath: binder.url });
                      close();
                    }
                  }}
                  tabindex="0"
                  role="button"
                >
                  <div class="sidebar-item-icon" style="background: {color};">
                    {(binder.name?.charAt(0) || 'B').toUpperCase()}
                  </div>
                  <div class="sidebar-item-content">
                    <div class="sidebar-item-name">{binder.name || 'Unknown'}</div>
                    {#if binder.owner}
                      <div class="sidebar-item-meta">{binder.owner}</div>
                    {/if}
                  </div>
                  <button
                    class="sidebar-pin-btn"
                    class:pinned={isPinned(binder.url)}
                    aria-label={isPinned(binder.url) ? 'Unpin binder' : 'Pin binder'}
                    onclick={(e) => {
                      e.stopPropagation();
                      togglePin(binder.url);
                    }}
                  >
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"
                      />
                    </svg>
                  </button>
                </div>
              </li>
            {/each}
          </ul>
        {:else}
          <p class="sidebar-empty">No recent binders</p>
        {/if}
      </div>
    </section>
  </div>
</nav>

<!-- Trade context menu (rendered outside nav to avoid z-index issues) -->
<ContextMenu
  isOpen={tradeMenuOpen}
  items={tradeMenuItems}
  title={tradeMenuTrade?.binderName || 'Trade'}
  anchorElement={tradeMenuAnchor}
  onClose={() => (tradeMenuOpen = false)}
/>

<style>
  /* CSS Variables */
  :root {
    --sidebar-width: 320px;
    --sidebar-width-mobile: 66vw;
    --sidebar-max-width: 400px;
    --sidebar-bg: #ffffff;
    --sidebar-header-bg: #f8fafc;
    --sidebar-border: #e2e8f0;
    --sidebar-text: #1e293b;
    --sidebar-text-secondary: #64748b;
    --sidebar-hover: #f1f5f9;
    --sidebar-active: #e2e8f0;
    --sidebar-transition: 300ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Hamburger Toggle */
  .sidebar-toggle {
    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 100;
    width: 44px;
    height: 44px;
    padding: 10px;
    background: var(--sidebar-bg);
    border: 1px solid var(--sidebar-border);
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition:
      transform 0.2s,
      box-shadow 0.2s;
  }

  .sidebar-toggle:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .sidebar-toggle:active {
    transform: scale(0.95);
  }

  .hamburger-line {
    width: 20px;
    height: 2px;
    background: var(--sidebar-text);
    border-radius: 2px;
    transition:
      transform 0.3s,
      opacity 0.3s;
  }

  .sidebar-toggle.open .hamburger-line:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }
  .sidebar-toggle.open .hamburger-line:nth-child(2) {
    opacity: 0;
  }
  .sidebar-toggle.open .hamburger-line:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  /* Backdrop */
  .sidebar-backdrop {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    visibility: hidden;
    transition:
      opacity var(--sidebar-transition),
      visibility var(--sidebar-transition);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  }

  .sidebar-backdrop.visible {
    opacity: 1;
    visibility: visible;
  }

  /* Sidebar Panel */
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 201;
    width: var(--sidebar-width);
    max-width: var(--sidebar-max-width);
    height: 100vh;
    height: 100dvh;
    background: var(--sidebar-bg);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
    transform: translateX(-100%);
    transition: transform var(--sidebar-transition);
    display: flex;
    flex-direction: column;
    will-change: transform;
  }

  .sidebar.open {
    transform: translateX(0);
  }

  @media (max-width: 767px) {
    .sidebar {
      width: var(--sidebar-width-mobile);
    }
  }

  /* Header */
  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: var(--sidebar-header-bg);
    border-bottom: 1px solid var(--sidebar-border);
    flex-shrink: 0;
  }

  .sidebar-logo {
    width: 28px;
    height: 28px;
    vertical-align: middle;
    margin-right: 0.35rem;
  }

  .sidebar-title {
    display: flex;
    align-items: center;
    font-size: 1.5rem;
    font-family: 'Georgia', serif;
    font-weight: 700;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    padding: 0;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .sidebar-title:hover {
    opacity: 0.7;
  }

  .sidebar-close {
    width: 36px;
    height: 36px;
    padding: 8px;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
  }

  .sidebar-close:hover {
    background: var(--sidebar-hover);
  }

  .sidebar-close svg {
    width: 20px;
    height: 20px;
    stroke: var(--sidebar-text-secondary);
    stroke-width: 2;
    fill: none;
  }

  /* Content */
  .sidebar-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .sidebar-content::-webkit-scrollbar {
    display: none;
  }

  .sidebar-section {
    padding: 0 20px;
    margin-bottom: 8px;
  }

  .sidebar-section-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--sidebar-text-secondary);
    margin: 0 0 12px 0;
  }

  .sidebar-divider {
    border: none;
    border-top: 1px solid var(--sidebar-border);
    margin: 16px 20px;
  }

  .sidebar-empty {
    padding: 12px;
    text-align: center;
    color: var(--sidebar-text-secondary);
    font-size: 13px;
    font-style: italic;
  }

  /* Search / Navigation Links */
  .sidebar-search-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    color: var(--sidebar-text);
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    transition: background 0.15s;
    cursor: pointer;
    background: transparent;
    border: none;
    width: 100%;
    text-align: left;
    font-family: inherit;
  }

  .sidebar-search-link:hover {
    background: var(--sidebar-hover);
  }

  .sidebar-search-link svg {
    opacity: 0.6;
    flex-shrink: 0;
  }

  /* Account Section - Signed Out */
  .sidebar-account-signed-out {
    text-align: center;
    padding: 16px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .sidebar-signin-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #5865f2;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition:
      background 0.2s,
      transform 0.2s;
    font-family: inherit;
  }

  .sidebar-signin-btn:hover {
    background: #4752c4;
    transform: translateY(-1px);
  }

  .sidebar-signin-btn:active {
    transform: translateY(0);
  }

  .sidebar-signin-hint {
    margin-top: 8px;
    font-size: 12px;
    color: var(--sidebar-text-secondary);
  }

  /* Account Section - Signed In */
  .sidebar-account-signed-in {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
  }

  .sidebar-user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--sidebar-hover);
    overflow: hidden;
    flex-shrink: 0;
  }

  .sidebar-user-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .sidebar-user-info {
    flex: 1;
    min-width: 0;
  }

  .sidebar-user-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--sidebar-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-user-email {
    font-size: 12px;
    color: var(--sidebar-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-signout-btn {
    padding: 6px 12px;
    background: transparent;
    border: 1px solid var(--sidebar-border);
    border-radius: 6px;
    font-size: 12px;
    color: var(--sidebar-text-secondary);
    cursor: pointer;
    transition:
      background 0.2s,
      border-color 0.2s;
    flex-shrink: 0;
    font-family: inherit;
  }

  .sidebar-signout-btn:hover {
    background: var(--sidebar-hover);
    border-color: var(--sidebar-text-secondary);
  }

  /* Subsections (Awaiting Exchange / In Progress) */
  .sidebar-subsection {
    margin-bottom: 16px;
  }

  .sidebar-subsection:last-child {
    margin-bottom: 0;
  }

  .sidebar-subsection-title {
    font-size: 11px;
    font-weight: 500;
    color: var(--sidebar-text-secondary);
    margin: 0 0 8px 4px;
    opacity: 0.8;
  }

  /* Exchange progress badge */
  .sidebar-exchange-progress {
    font-size: 11px;
    color: var(--sidebar-text-secondary);
    background: rgba(34, 197, 94, 0.15);
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: auto;
  }

  /* Sidebar List (shared by trades + binders) */
  .sidebar-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .sidebar-list-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    margin: 0 -12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
    background: transparent;
    border: none;
    width: calc(100% + 24px);
    text-align: left;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }

  .sidebar-list-item:hover {
    background: var(--sidebar-hover);
  }

  .sidebar-list-item:active {
    background: var(--sidebar-active);
  }

  .sidebar-list-item.active-trade {
    background: rgba(34, 197, 94, 0.08);
    border-left: 3px solid #22c55e;
    padding-left: 9px;
  }

  /* Icon circle (letter avatar for binders, trade icon for trades) */
  .sidebar-item-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    color: white;
  }

  .sidebar-item-content {
    flex: 1;
    min-width: 0;
  }

  .sidebar-item-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--sidebar-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-item-meta {
    font-size: 12px;
    color: var(--sidebar-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Trade menu button in sidebar */
  .sidebar-trade-menu-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    opacity: 0;
    transition:
      opacity 0.2s,
      background 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--sidebar-text-secondary);
    font-size: 18px;
  }

  .sidebar-list-item:hover .sidebar-trade-menu-btn {
    opacity: 0.6;
  }

  .sidebar-trade-menu-btn:hover {
    opacity: 1 !important;
    background: var(--sidebar-hover);
  }

  /* Always show on mobile (no hover) */
  @media (max-width: 767px) {
    .sidebar-trade-menu-btn {
      opacity: 0.6;
    }
  }

  /* Star Pin Button */
  .sidebar-pin-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    opacity: 0.4;
    transition:
      opacity 0.2s,
      background 0.2s,
      transform 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--sidebar-text-secondary);
    touch-action: manipulation;
  }

  .sidebar-pin-btn:hover {
    opacity: 0.8;
    background: var(--sidebar-hover);
  }

  .sidebar-pin-btn.pinned {
    opacity: 1;
    color: #f59e0b;
  }

  .sidebar-pin-btn.pinned:hover {
    transform: scale(1.1);
  }

  .sidebar-pin-btn svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  /* My Binders slot count */
  .sidebar-slot-count {
    font-weight: 400;
    opacity: 0.6;
    font-size: 11px;
    margin-left: 4px;
  }

  /* Drag-and-drop feedback */
  .sidebar-drop-zone {
    transition:
      background 0.2s,
      border-color 0.2s;
    border-radius: 8px;
    min-height: 24px;
    border: 2px dashed transparent;
    margin-left: -8px;
    padding-left: 8px;
  }

  :global(.sidebar-drop-zone.drag-over) {
    background: rgba(59, 130, 246, 0.06);
    border-color: rgba(59, 130, 246, 0.4);
  }

  :global(.dragging) {
    opacity: 0;
    pointer-events: none;
  }
</style>
